<template>
  <main class="guard-menu">
    <div class="guard-menu__header">
      <div class="guard-menu__title">¿En qué podemos ayudarte?</div>
      <div class="guard-menu__sub">
        Toca una opción o escribe tu pregunta abajo
      </div>
    </div>

    <div class="guard-menu__search-wrap">
      <div class="guard-menu__search-bar">
        <svg viewBox="0 0 24 24" class="guard-menu__search-icon">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" stroke-linecap="round" />
        </svg>
        <input
          ref="searchInput"
          type="text"
          class="guard-menu__search-input"
          placeholder="Ejemplo: ¿Cómo entro con mi carro?"
          autocomplete="off"
          v-model="searchQuery"
          @keydown.enter="onSearchEnter"
        />
        <button class="guard-menu__search-go" @click="onSearchEnter">
          Buscar
        </button>
      </div>
      <div
        class="guard-menu__search-results"
        :class="{ show: searchQuery.length > 0 && showResults }"
        ref="searchResults"
      >
        <button
          v-for="result in filteredQuestions"
          :key="result.feature.code + '-' + result.question.id"
          class="guard-menu__search-item"
          @click="selectResult(result)"
        >
          <img :src="result.feature.logo" :alt="result.feature.name" />
          <span class="guard-menu__search-item-text">
            <span class="guard-menu__search-item-q">{{
              result.question.title
            }}</span>
            <span class="guard-menu__search-item-cat">{{
              result.feature.name
            }}</span>
          </span>
        </button>
        <div
          v-if="filteredQuestions.length === 0"
          class="guard-menu__search-empty"
        >
          No encontramos resultados para <b>"{{ searchQuery }}"</b>. Toca
          <b>Llamar a recepción</b> si necesitas ayuda inmediata.
        </div>
      </div>
    </div>
    <CompanyFeatures
      :features="features"
      @select-feature="$emit('select-feature', $event)"
    />

    <div class="guard-menu__footer">
      <b>fuseaccess</b>&nbsp;· Vigilante Virtual v23.0
    </div>
  </main>
</template>

<script>
import CompanyFeatures from "./CompanyFeatures.vue";

export default {
  name: "Menu",
  components: {
    CompanyFeatures,
  },
  data() {
    return {
      searchQuery: "",
      allResults: [],
      filteredQuestions: [],
      showResults: true,
    };
  },
  props: {
    features: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    features: {
      handler() {
        this.buildSearchIndex();
      },
      immediate: true,
    },
    searchQuery(newQuery) {
      if (!newQuery || newQuery.trim() === "") {
        this.filteredQuestions = [];
        this.showResults = true;
        return;
      }
      const q = newQuery.toLowerCase();
      this.filteredQuestions = this.allResults.filter((r) =>
        r.question.title.toLowerCase().includes(q)
      );
      this.showResults = true;
    },
  },
  methods: {
    buildSearchIndex() {
      this.allResults = this.features.flatMap((feature) =>
        (feature.help_questions || []).map((question) => ({
          feature,
          question,
        }))
      );
    },
    onSearchEnter() {
      // pick first result when clicking enter
      if (this.filteredQuestions.length > 0) {
        this.selectResult(this.filteredQuestions[0]);
      }
    },
    selectResult(result) {
      if (this.$refs.searchInput) this.$refs.searchInput.blur();

      this.searchQuery = "";
      this.filteredQuestions = [];

      this.$emit("select-question", result.question, result.feature);
    },
    closeResults() {
      this.showResults = false;
    },
    onDocumentClick(e) {
      const resultsContainer = this.$el.querySelector(
        ".guard-menu__search-wrap"
      );
      // close the results list when clicking outside of it
      if (resultsContainer && !resultsContainer.contains(e.target)) {
        this.closeResults();
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
  },
};
</script>

<style scoped lang="scss">
@import "../styles";

.guard-menu {
  @include guard-panel-help;
  @include guard-scrollbar(6px);

  &__header {
    margin-bottom: 1.2rem;
    @include guard-responsive-width;
    text-align: center;
  }
  &__title {
    font-weight: 600;
    font-size: 1.5rem;
    color: $guard-ink-900;
  }
  &__sub {
    font-size: 0.92rem;
    color: $guard-ink-600;
    margin-top: 0.3rem;
    font-weight: 400;
  }

  &__search-wrap {
    position: relative;
    @include guard-responsive-width;
    margin: 0 auto 1.5rem;
  }
  &__search-bar {
    @include guard-search-bar;
  }
  &__search-icon {
    width: 1.1rem;
    height: 1.1rem;
    stroke: $guard-ink-400;
    fill: none;
    stroke-width: 1.8;
    flex-shrink: 0;
  }
  &__search-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    font-size: 0.98rem;
    font-family: inherit;
    font-weight: 400;
    color: $guard-ink-900;
    padding: 0.6rem 0;
    background: transparent;
    &::placeholder {
      color: $guard-ink-400;
    }
  }
  &__search-go {
    @include guard-button($guard-navy-900, $guard-navy-800, $guard-radius-pill);
    padding: 0.65rem 1.15rem;
    font-size: 0.85rem;
  }

  &__search-results {
    @include guard-search-results;
  }

  &__search-item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    text-align: left;
    background: none;
    border: none;
    border-radius: $guard-radius-sm;
    padding: 0.6rem;
    cursor: pointer;
    transition: background $guard-anim-fast;

    &:hover {
      background: $guard-bg-app;
    }
    img {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: $guard-radius-sm;
      background: $guard-cyan-100;
      object-fit: contain;
      padding: 0.3rem;
      flex-shrink: 0;
    }
  }
  &__search-item-text {
    min-width: 0;
  }
  &__search-item-q {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    color: $guard-ink-900;
    line-height: 1.3;
  }
  &__search-item-cat {
    display: block;
    font-size: 0.72rem;
    color: $guard-cyan-600;
    font-weight: 500;
    margin-top: 0.1rem;
  }
  &__search-empty {
    padding: 1rem 0.8rem;
    text-align: center;
    color: $guard-ink-600;
    font-size: 0.88rem;
    b {
      color: $guard-ink-900;
    }
  }

  &__footer {
    margin-top: auto;
    padding-top: 1.4rem;
    @include guard-flex-center;
    gap: 0.4rem;
    color: $guard-ink-400;
    font-size: 0.74rem;
    letter-spacing: 0.02em;
    b {
      color: $guard-ink-600;
      font-weight: 500;
    }
  }
}

@media (max-width: $guard-bp-sm) {
  .guard-menu {
    padding: 1.2rem 1rem 1rem;

    &__search-bar {
      padding: 0.3rem 0.3rem 0.3rem 0.8rem;
    }
    &__search-go {
      padding: 0.6rem 0.8rem;
      font-size: 0.78rem;
    }
  }
}
</style>
